import http from "../../axios/http-common";

/**
 *  Common interface for all KeaApi objects.
 */
type KeaApiObject = {
  id: number;
  updatedAt: string;
  createdAt: string;
};

export interface IKeaApi<InType, OutType> {
  elements: Array<OutType>;
  getAll: () => Promise<boolean>;
  create: (data: InType) => Promise<number | undefined>;
  update: (data: InType) => Promise<boolean>;
  deleteFunction: (id: number) => Promise<boolean>;
}

/**
 * This function is used to create a new KeaApi object.
 * It takes elements and setElements as parameters to manage the state of the elements; this allows you to use
 * useState and let react manage the state of the elements and rerenders.
 */
export const useKeaApi = <InType extends KeaApiObject, OutType extends KeaApiObject>(
  apiEndpointTmp: string,
  ignoreInputKeys: string[],
  elements: OutType[],
  setElements: (elements: OutType[]) => void,
): IKeaApi<InType, OutType> => {
  const apiEndpoint: string = apiEndpointTmp.endsWith("/") ? apiEndpointTmp : apiEndpointTmp + "/";

  /**
   * This function is used to fetch all elements from the backend.
   * It is **not** necessariy to call this after creating or updating an element; in these cases it is called automatically!
   */
  const getAll = async (): Promise<boolean> => {
    try {
      console.info("Fetching data from " + apiEndpoint);
      const request = http.get(apiEndpoint);
      const response = await request;
      if (response.status === 200) {
        setElements(response.data.data);
        console.info("Fetched data from " + apiEndpoint + ", length: " + response.data.data.length);
        return true;
      } else {
        console.warn("Error fetching data" + JSON.stringify(response));
        return false;
      }
    } catch (e) {
      console.warn("Error fetching data" + JSON.stringify(e));
      return false;
    }
  };

  /**
   * Creates the new element, ignoring its id, creation and update time.
   */
  const create = async (data: InType): Promise<number | undefined> => {
    // Create a copy of data with only the attributes present in InType
    const dataCopy: Partial<InType> = {} as Partial<InType>;

    Object.keys(data).forEach((key) => {
      if (!ignoreInputKeys.includes(key)) dataCopy[key as keyof InType] = data[key as keyof InType];
    });

    // remove id, createdAt and updatedAt from data since they are managed by the backend and not allowed in the request
    delete dataCopy.id;
    delete dataCopy.updatedAt;
    delete dataCopy.createdAt;

    try {
      const request = http.post(apiEndpoint, dataCopy);
      const response = await request;

      if (response.status === 201 && response.data.success) {
        await getAll();
        return response.data.data.id;
      } else {
        console.warn("Error during element creation:" + JSON.stringify(response));
        return undefined;
      }
    } catch (e) {
      console.warn("Error during element creation:" + JSON.stringify(e));
      return undefined;
    }
  };

  /**
   *  Updates the element, ignoring its creation and update time.
   *  The id of the object to update is taken from the data object!
   */
  const update = async (data: InType): Promise<boolean> => {
    // get id from the data
    const id = data.id;

    // Create a copy of data with only the attributes present in InType
    const dataCopy: Partial<InType> = {} as Partial<InType>;
    Object.keys(data).forEach((key) => {
      if (!ignoreInputKeys.includes(key)) dataCopy[key as keyof InType] = data[key as keyof InType];
    });

    // remove id, createdAt and updatedAt from data since they are managed by the backend and not allowed in the request
    delete dataCopy.id;
    delete dataCopy.updatedAt;
    delete dataCopy.createdAt;

    try {
      const request = http.put(apiEndpoint + id.toString(), dataCopy);
      const response = await request;

      if (response.status === 200 && response.data.success) {
        await getAll();
        return true;
      } else {
        console.warn("Error during element update:" + JSON.stringify(response));
        return false;
      }
    } catch (e) {
      console.warn("Error during element update:" + JSON.stringify(e));
      return false;
    }
  };

  /**
   * Deletes the element with the given id. True if successful, else false.
   */
  const deleteFunction = async (id: number): Promise<boolean> => {
    try {
      const request = http.delete(apiEndpoint + id.toString());
      const response = await request;

      if (response.status == 200 && response.data.success) {
        await getAll();
        return true;
      } else {
        console.warn("Error during element deletion:" + JSON.stringify(response));
        return false;
      }
    } catch (e) {
      console.warn("Error during element deletion:" + JSON.stringify(e));
      return false;
    }
  };

  return { elements: elements, getAll, create, update, deleteFunction };
};

/**
 * This function is used to create a dummy KeaApi object.
 * It does nothing, holds an empty array, and logs a warning if a function is called.
 */
export const DummyKeaApi = <InType extends KeaApiObject, OutType extends KeaApiObject>(
  apiEndpoint: string,
): IKeaApi<InType, OutType> => {
  return {
    elements: [],
    getAll: async () => {
      console.warn(`Context for ${apiEndpoint} not in scope.`);
      return false;
    },
    create: async (_data: InType) => {
      console.warn(`Context for ${apiEndpoint} not in scope.`);
      return undefined;
    },
    update: async (_data: InType) => {
      console.warn(`Context for ${apiEndpoint} not in scope.`);
      return false;
    },
    deleteFunction: async (_id: number) => {
      console.warn(`Context for ${apiEndpoint} not in scope.`);
      return false;
    },
  };
};
